import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { formatDateTimeLocal } from '@/utils/timezone'
import { PHOTO_ACTIVITY } from '../api'
import type { PhotoActivityEntry } from '../types'

const ACTION_KEYS: Record<string, string> = {
  uploaded: 'Photos_activity_uploaded',
  updated: 'Photos_activity_updated',
  visibility_changed: 'Photos_activity_visibility_changed',
  moved: 'Photos_activity_moved',
  deleted: 'Photos_activity_deleted',
  reprocessed: 'Photos_activity_reprocessed',
  snag_created: 'Photos_activity_snag_created',
  folder_created: 'Photos_activity_folder_created',
  folder_renamed: 'Photos_activity_folder_renamed',
  folder_moved: 'Photos_activity_folder_moved',
  folder_deleted: 'Photos_activity_folder_deleted',
  album_created: 'Photos_activity_album_created',
  album_updated: 'Photos_activity_album_updated',
  album_deleted: 'Photos_activity_album_deleted',
  album_photos_added: 'Photos_activity_album_photos_added',
  album_photos_removed: 'Photos_activity_album_photos_removed',
  tag_created: 'Photos_activity_tag_created',
  tag_updated: 'Photos_activity_tag_updated',
  tags_merged: 'Photos_activity_tags_merged',
  tags_deleted: 'Photos_activity_tags_deleted',
  report_generated: 'Photos_activity_report_generated',
}

interface Props {
  photoId: string
}

const PhotoActivityList: React.FC<Props> = ({ photoId }) => {
  const { t, i18n } = useTranslation()
  const { data, loading } = useQuery<{ photoActivity: PhotoActivityEntry[] }>(PHOTO_ACTIVITY, {
    variables: { photoId },
    fetchPolicy: 'cache-and-network',
  })
  const entries = data?.photoActivity ?? []

  if (loading && entries.length === 0) return <p className="text-sm text-muted-foreground">{t('Common_loading')}</p>
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">{t('Photos_activity_none')}</p>

  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <li key={entry.id} className="text-sm">
          <div>{ACTION_KEYS[entry.action] ? t(ACTION_KEYS[entry.action]) : entry.action}</div>
          <div className="text-xs text-muted-foreground">
            {entry.user?.name ? `${entry.user.name} · ` : ''}
            {formatDateTimeLocal(entry.createdAt, i18n.language)}
          </div>
        </li>
      ))}
    </ul>
  )
}

export default PhotoActivityList
